import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'pt', label: 'PT' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const isHome = location.pathname === '/';

  const anchorLinks = [
    { to: '#categories', label: t('categories_title') },
    { to: '#guides', label: t('nav_guides') },
    { to: '#faq', label: t('faq_title') },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-foreground-950">
              CSSBuy<span className="text-primary-600">BR</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav_home')}
            </Link>

            <Link
              to="/article/cssbuy-spreadsheet-guide"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav_shopping_guide')}
            </Link>

            {anchorLinks.map((link) => (
              <a
                key={link.to}
                href={isHome ? link.to : `/${link.to}`}
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}

            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors"
              >
                <i className="ri-global-line w-4 h-4 flex items-center justify-center" />
                {i18n.language.toUpperCase()}
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border border-background-200 rounded-lg shadow-lg overflow-hidden">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${
                        i18n.language === lang.code ? 'text-primary-600 font-semibold' : 'text-foreground-700'
                      }`}
                    >
                      {t(`lang_${lang.code}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              CSSBuy Spreadsheet
              <i className="ri-external-link-line w-3 h-3 flex items-center justify-center" />
            </a>
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-foreground-900"
            aria-label="Menu"
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line w-6 h-6 flex items-center justify-center text-2xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-background-200 px-4 py-4 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-3 py-2.5 bg-primary-50 text-primary-700 text-sm font-semibold rounded-md"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" />
              {t('nav_home')}
            </Link>
            <Link
              to="/article/cssbuy-spreadsheet-guide"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-3 py-2.5 bg-background-100 text-foreground-900 text-sm font-semibold rounded-md"
            >
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center" />
              {t('nav_shopping_guide')}
            </Link>
          </div>

          <div>
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              {t('categories_title')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-6 h-6 rounded object-cover flex-shrink-0"
                  />
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {anchorLinks.map((link) => (
              <a
                key={link.to}
                href={isHome ? link.to : `/${link.to}`}
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2 bg-background-100 text-foreground-700 text-xs font-medium rounded-md text-center"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500 mb-2">{t('nav_language')}</p>
            <div className="flex gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { changeLanguage(lang.code); setMenuOpen(false); }}
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    i18n.language === lang.code
                      ? 'border-primary-500 text-primary-600 font-semibold'
                      : 'border-background-300 text-foreground-600'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block w-full text-center px-4 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-md"
          >
            CSSBuy Spreadsheet
          </a>
        </div>
      )}
    </nav>
  );
}